/**
 * Which EMR modules are switched on for the current clinic and user.
 *
 * This store used to be localStorage-only, which made every module gate in
 * the app — `FeatureGuard`, the sidebar's `moduleKey` filter,
 * `ExplorePage.canSee()` — a per-browser preference. It didn't follow a
 * doctor to a second device and an owner couldn't set it for their staff.
 * It is now server-backed: `refresh()` pulls the clinic's choices from
 * `/system-settings/frontend/modules`, and localStorage stays only as an
 * offline cache so a reload before the fetch lands doesn't blank the nav.
 *
 * `isModuleEnabled(key)` is unchanged on purpose — every existing call
 * site keeps working; only where the answer comes from moved.
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  FeatureToggleStore,
  ModuleKey,
  ModuleToggle,
} from '../types/featureToggle';
import {
  getModuleSettings,
  updateModuleSettings,
} from '../services/api/systemSettingsApi';

const getDefaultModules = (): Record<ModuleKey, ModuleToggle> => ({
  [ModuleKey.PATIENT_MANAGEMENT]: {
    key: ModuleKey.PATIENT_MANAGEMENT,
    name: 'Patient Management',
    description: 'Patient demographics, medical history, and records',
    enabled: true,
    features: [
      {
        key: 'patient_registration',
        name: 'Patient Registration',
        description: 'Register new patients',
        enabled: true,
      },
      {
        key: 'medical_history',
        name: 'Medical History',
        description: 'View and edit patient medical history',
        enabled: true,
      },
      {
        key: 'insurance_management',
        name: 'Insurance Management',
        description: 'Manage patient insurance information',
        enabled: true,
      },
      {
        key: 'photo_management',
        name: 'Photo Management',
        description: 'Upload and manage patient photos',
        enabled: true,
      },
    ],
  },
  [ModuleKey.APPOINTMENTS]: {
    key: ModuleKey.APPOINTMENTS,
    name: 'Appointments & Scheduling',
    description: 'Appointment scheduling and resource management',
    enabled: true,
    dependsOn: [ModuleKey.PATIENT_MANAGEMENT],
    features: [
      {
        key: 'appointment_booking',
        name: 'Appointment Booking',
        description: 'Create and manage appointments',
        enabled: true,
      },
      {
        key: 'resource_scheduling',
        name: 'Resource Scheduling',
        description: 'Schedule hospital resources',
        enabled: false,
      },
      {
        key: 'provider_availability',
        name: 'Provider Availability',
        description: 'Manage provider schedules',
        enabled: true,
      },
      {
        key: 'waitlist',
        name: 'Waitlist Management',
        description: 'Manage appointment waitlists',
        enabled: true,
      },
    ],
  },
  [ModuleKey.MEDICAL_RECORDS]: {
    key: ModuleKey.MEDICAL_RECORDS,
    name: 'Medical Records',
    description: 'Clinical documentation and notes',
    enabled: true,
    dependsOn: [ModuleKey.PATIENT_MANAGEMENT],
    features: [
      {
        key: 'consultation_notes',
        name: 'Consultation Notes',
        description: 'Create consultation documentation',
        enabled: true,
      },
      {
        key: 'vitals_tracking',
        name: 'Vitals Tracking',
        description: 'Record patient vital signs',
        enabled: true,
      },
      {
        key: 'diagnosis',
        name: 'Diagnosis Entry',
        description: 'Record patient diagnoses',
        enabled: true,
      },
      {
        key: 'treatment_plans',
        name: 'Treatment Plans',
        description: 'Create and manage treatment plans',
        enabled: true,
      },
    ],
  },
  [ModuleKey.LAB_ORDERS]: {
    key: ModuleKey.LAB_ORDERS,
    name: 'Lab Orders',
    description: 'Laboratory order and results management',
    enabled: true,
    dependsOn: [ModuleKey.PATIENT_MANAGEMENT],
    features: [
      {
        key: 'order_creation',
        name: 'Order Creation',
        description: 'Create lab orders',
        enabled: true,
      },
      {
        key: 'specimen_tracking',
        name: 'Specimen Tracking',
        description: 'Track specimen collection and processing',
        enabled: true,
      },
      {
        key: 'results_management',
        name: 'Results Management',
        description: 'View and manage lab results',
        enabled: true,
      },
    ],
  },
  [ModuleKey.IMAGING_ORDERS]: {
    key: ModuleKey.IMAGING_ORDERS,
    name: 'Imaging Orders',
    description: 'Radiology and imaging order management',
    enabled: true,
    dependsOn: [ModuleKey.PATIENT_MANAGEMENT],
    features: [
      {
        key: 'order_creation',
        name: 'Order Creation',
        description: 'Create imaging orders',
        enabled: true,
      },
      {
        key: 'results_viewing',
        name: 'Results Viewing',
        description: 'View imaging studies and reports',
        enabled: true,
      },
    ],
  },
  // [ModuleKey.CPOE]: {
  //   key: ModuleKey.CPOE,
  //   name: 'CPOE (Orders)',
  //   description: 'Computerized Physician Order Entry',
  //   enabled: true,
  //   dependsOn: [ModuleKey.PATIENT_MANAGEMENT],
  //   features: [
  //     {
  //       key: 'medication_orders',
  //       name: 'Medication Orders',
  //       description: 'Create medication orders',
  //       enabled: true,
  //     },
  //     {
  //       key: 'imaging_orders',
  //       name: 'Imaging Orders',
  //       description: 'Order imaging studies',
  //       enabled: true,
  //     },
  //     {
  //       key: 'order_sets',
  //       name: 'Order Sets',
  //       description: 'Use predefined order sets',
  //       enabled: true,
  //     },
  //   ],
  // },
  // [ModuleKey.E_PRESCRIBING]: {
  //   key: ModuleKey.E_PRESCRIBING,
  //   name: 'E-Prescribing',
  //   description: 'Electronic prescription management',
  //   enabled: true,
  //   dependsOn: [ModuleKey.PATIENT_MANAGEMENT],
  //   features: [
  //     {
  //       key: 'prescription_creation',
  //       name: 'Prescription Creation',
  //       description: 'Create electronic prescriptions',
  //       enabled: true,
  //     },
  //     {
  //       key: 'refill_requests',
  //       name: 'Refill Requests',
  //       description: 'Manage prescription refills',
  //       enabled: true,
  //     },
  //     {
  //       key: 'pharmacy_network',
  //       name: 'Pharmacy Network',
  //       description: 'Connect with pharmacy network',
  //       enabled: true,
  //     },
  //   ],
  // },
  // [ModuleKey.ADT]: {
  //   key: ModuleKey.ADT,
  //   name: 'ADT (Admission/Discharge/Transfer)',
  //   description: 'Patient admission, discharge, and transfer workflows',
  //   enabled: false,
  //   dependsOn: [ModuleKey.PATIENT_MANAGEMENT],
  //   features: [
  //     {
  //       key: 'admissions',
  //       name: 'Patient Admissions',
  //       description: 'Admit patients to hospital',
  //       enabled: true,
  //     },
  //     {
  //       key: 'discharges',
  //       name: 'Patient Discharges',
  //       description: 'Discharge patients from hospital',
  //       enabled: true,
  //     },
  //     {
  //       key: 'transfers',
  //       name: 'Patient Transfers',
  //       description: 'Transfer patients between departments',
  //       enabled: true,
  //     },
  //   ],
  // },
  // [ModuleKey.BED_MANAGEMENT]: {
  //   key: ModuleKey.BED_MANAGEMENT,
  //   name: 'Bed Management',
  //   description: 'Hospital bed allocation and tracking',
  //   enabled: false,
  //   dependsOn: [ModuleKey.PATIENT_MANAGEMENT, ModuleKey.ADT],
  //   features: [
  //     {
  //       key: 'bed_allocation',
  //       name: 'Bed Allocation',
  //       description: 'Assign beds to patients',
  //       enabled: true,
  //     },
  //     {
  //       key: 'capacity_management',
  //       name: 'Capacity Management',
  //       description: 'Monitor hospital capacity',
  //       enabled: true,
  //     },
  //   ],
  // },
  // [ModuleKey.CENSUS]: {
  //   key: ModuleKey.CENSUS,
  //   name: 'Census Management',
  //   description: 'Hospital census and occupancy reporting',
  //   enabled: false,
  //   dependsOn: [ModuleKey.PATIENT_MANAGEMENT, ModuleKey.ADT],
  //   features: [
  //     {
  //       key: 'census_reporting',
  //       name: 'Census Reporting',
  //       description: 'Generate census reports',
  //       enabled: true,
  //     },
  //     {
  //       key: 'occupancy_tracking',
  //       name: 'Occupancy Tracking',
  //       description: 'Track hospital occupancy rates',
  //       enabled: true,
  //     },
  //   ],
  // },
  [ModuleKey.BILLING]: {
    key: ModuleKey.BILLING,
    name: 'Billing',
    description: 'Billing and revenue cycle management',
    enabled: true,
    dependsOn: [ModuleKey.PATIENT_MANAGEMENT],
    features: [
      {
        key: 'invoice_generation',
        name: 'Invoice Generation',
        description: 'Generate patient invoices',
        enabled: true,
      },
      {
        key: 'payment_processing',
        name: 'Payment Processing',
        description: 'Process patient payments',
        enabled: true,
      },
      {
        key: 'insurance_claims',
        name: 'Insurance Claims',
        description: 'Submit and track insurance claims',
        enabled: true,
      },
    ],
  },
  [ModuleKey.CLINICAL_DECISION_SUPPORT]: {
    key: ModuleKey.CLINICAL_DECISION_SUPPORT,
    name: 'Clinical Decision Support',
    description: 'Drug interactions, allergies, and clinical protocols',
    enabled: true,
    features: [
      {
        key: 'drug_interactions',
        name: 'Drug Interaction Checking',
        description: 'Check for drug interactions',
        enabled: true,
      },
      {
        key: 'allergy_checking',
        name: 'Allergy Checking',
        description: 'Check patient allergies',
        enabled: true,
      },
      {
        key: 'clinical_protocols',
        name: 'Clinical Protocols',
        description: 'Access clinical guidelines',
        enabled: true,
      },
    ],
  },
  [ModuleKey.TEMPLATES]: {
    key: ModuleKey.TEMPLATES,
    name: 'Clinical Templates',
    description: 'Reusable clinical documentation templates',
    enabled: true,
    features: [
      {
        key: 'template_builder',
        name: 'Template Builder',
        description: 'Create custom templates',
        enabled: true,
      },
      {
        key: 'template_library',
        name: 'Template Library',
        description: 'Access template library',
        enabled: true,
      },
    ],
  },
  [ModuleKey.VOICE_AI]: {
    key: ModuleKey.VOICE_AI,
    name: 'Voice & AI Features',
    description: 'Voice dictation and AI assistance',
    enabled: true,
    features: [
      {
        key: 'voice_dictation',
        name: 'Voice Dictation',
        description: 'Voice-to-text dictation',
        enabled: true,
      },
      {
        key: 'ambient_listening',
        name: 'Ambient Listening',
        description: 'Ambient clinical documentation',
        enabled: true,
      },
      {
        key: 'ai_assistant',
        name: 'AI Assistant',
        description: 'AI-powered clinical assistant',
        enabled: true,
      },
      {
        key: 'ai_command_palette',
        name: 'AI Command Palette',
        description: 'Quick AI commands interface',
        enabled: true,
      },
    ],
  },
});

export const useFeatureToggleStore = create<FeatureToggleStore>()(
  persist(
    (set, get) => ({
      modules: getDefaultModules(),
      lastUpdated: new Date().toISOString(),
      updatedBy: undefined,
      loaded: false,
      saving: false,
      saveError: null,

      refresh: async () => {
        try {
          const response = await getModuleSettings();
          const settings = response?.data ?? {};
          set((state) => {
            const next = { ...state.modules };
            // Apply only keys the clinic has actually chosen. An absent
            // key keeps the module's default — a clinic that has never
            // opened the settings screen gets the shipped experience, not
            // an EMR with every module switched off.
            for (const [key, enabled] of Object.entries(settings)) {
              const existing = next[key as ModuleKey];
              if (!existing) continue; // a key from a newer/older release
              next[key as ModuleKey] = { ...existing, enabled };
            }
            return { modules: next, loaded: true };
          });
        } catch {
          // Leave whatever is on screen — defaults, or the localStorage
          // cache from the last good load. Blanking the navigation on a
          // transient network blip is worse than briefly stale settings.
          set({ loaded: true });
        }
      },

      save: async () => {
        // Refuse to write defaults over a clinic's real settings when the
        // read never landed. The admin screen surfaces `saveError`.
        if (!get().loaded) {
          set({ saveError: 'Settings have not loaded yet.' });
          return;
        }
        set({ saving: true, saveError: null });
        try {
          const payload = Object.fromEntries(
            Object.values(get().modules).map((m) => [m.key, m.enabled]),
          );
          await updateModuleSettings(payload);
          set({ saving: false, lastUpdated: new Date().toISOString() });
        } catch (error) {
          set({
            saving: false,
            saveError:
              error instanceof Error ? error.message : 'Could not save modules',
          });
        }
      },

      initializeDefaults: () => {
        set({
          modules: getDefaultModules(),
          lastUpdated: new Date().toISOString(),
        });
      },

      toggleModule: (moduleKey: ModuleKey, enabled: boolean) => {
        set((state) => ({
          modules: {
            ...state.modules,
            [moduleKey]: {
              ...state.modules[moduleKey],
              enabled,
            },
          },
          lastUpdated: new Date().toISOString(),
        }));
      },

      toggleFeature: (
        moduleKey: ModuleKey,
        featureKey: string,
        enabled: boolean
      ) => {
        set((state) => {
          const module = state.modules[moduleKey];
          const updatedFeatures = module.features.map((feature) =>
            feature.key === featureKey ? { ...feature, enabled } : feature
          );

          return {
            modules: {
              ...state.modules,
              [moduleKey]: {
                ...module,
                features: updatedFeatures,
              },
            },
            lastUpdated: new Date().toISOString(),
          };
        });
      },

      isModuleEnabled: (moduleKey: ModuleKey) => {
        const module = get().modules[moduleKey];
        return module?.enabled ?? false;
      },

      isFeatureEnabled: (moduleKey: ModuleKey, featureKey: string) => {
        const module = get().modules[moduleKey];
        if (!module?.enabled) return false;

        const feature = module.features.find((f) => f.key === featureKey);
        return feature?.enabled ?? false;
      },

      resetToDefaults: () => {
        set({
          modules: getDefaultModules(),
          lastUpdated: new Date().toISOString(),
        });
      },

      // Get all modules that depend on the given module
      getDependentModules: (moduleKey: ModuleKey) => {
        const modules = get().modules;
        const dependent: ModuleKey[] = [];

        Object.values(modules).forEach((module) => {
          if (module.dependsOn && module.dependsOn.includes(moduleKey)) {
            dependent.push(module.key);
          }
        });

        return dependent;
      },

      // Get dependencies that are missing (disabled) for the given module
      getMissingDependencies: (moduleKey: ModuleKey) => {
        const modules = get().modules;
        const module = modules[moduleKey];

        if (!module.dependsOn) return [];

        return module.dependsOn.filter(
          (depKey) => !modules[depKey]?.enabled
        );
      },

      // Check if a module can be disabled (no enabled modules depend on it)
      canDisableModule: (moduleKey: ModuleKey) => {
        const modules = get().modules;
        const dependentModules = get().getDependentModules(moduleKey);

        // Find which dependent modules are currently enabled
        const enabledDependents = dependentModules.filter(
          (depKey) => modules[depKey]?.enabled
        );

        return {
          canDisable: enabledDependents.length === 0,
          dependentModules: enabledDependents,
        };
      },

      // Check if a module can be enabled (all dependencies are enabled)
      canEnableModule: (moduleKey: ModuleKey) => {
        const missingDependencies = get().getMissingDependencies(moduleKey);

        return {
          canEnable: missingDependencies.length === 0,
          missingDependencies,
        };
      },
    }),
    {
      name: 'feature-toggle-storage',
      // The default persist merge is shallow, so a persisted `modules`
      // object wholesale replaces the default. That means any module
      // added to `getDefaultModules()` after a user first persisted is
      // missing from their state forever, and `isModuleEnabled` returns
      // `false` for it. Merge per-module so new defaults backfill into
      // existing localStorage while user toggles are preserved.
      // Only the module map is worth caching. `loaded` in particular must
      // NOT be restored: a rehydrated `true` would let `save()` push a
      // stale cache over the clinic's server settings before the fetch
      // for this session has landed.
      partialize: (state) =>
        ({
          modules: state.modules,
          lastUpdated: state.lastUpdated,
        }) as unknown as FeatureToggleStore,
      merge: (persisted, current) => {
        const p = (persisted ?? {}) as Partial<FeatureToggleStore>;
        return {
          ...current,
          ...p,
          modules: {
            ...current.modules,
            ...(p.modules ?? {}),
          },
          loaded: false,
          saving: false,
          saveError: null,
        };
      },
    }
  )
);
